<template>
  <div class="!pt-0">
    <div class="max-w-7xl mx-auto flex flex-col max-h-screen pt-16 pb-2.5">
      <div class="my-2.5">
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('HeadContract.IndirectProgressClaimsIndex.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.IndirectProgressClaimsIndex.subTitle') }}
            </p>
          </div>

          <div
            v-if="!latestClaim || latestClaim?.status === ClaimStatus.certified"
            class="flex items-center space-x-2"
          >
            <button
              type="button"
              class="btn btn--secondary-blue flex items-center space-x-2"
              @click.prevent="show = true"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

              <span>{{ $t('HeadContract.IndirectProgressClaimsIndex.button.newClaim') }}</span>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="claims.length === 0"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="px-6 py-16 text-center">
          <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.IndirectProgressClaimsIndex.noClaimsFound') }}
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            {{ $t('HeadContract.IndirectProgressClaimsIndex.createFirstClaim') }}
          </p>

          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="show = true"
          >
            {{ $t('HeadContract.IndirectProgressClaimsIndex.button.createFirstClaim') }}
          </button>
        </div>
      </div>

      <div
        v-else
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto relative"
      >
        <!-- Table Header with Summary -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
          <div class="space-y-4">
            <!-- Header -->
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-gray-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.claims') }}
                </h3>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="animate__animated animate__fadeIn animate__faster"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
              >
                <p
                  v-if="claims.length > 0"
                  key="total-claims"
                  class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                >
                  <span class="font-medium text-gray-900">{{ claims.length }}&nbsp;</span>

                  <span class="text-gray-500"> {{ $t('HeadContract.IndirectProgressClaimsIndex.claim', claims.length) }}</span>
                </p>
              </Transition>
            </div>

            <!-- Summary Row -->
            <div class="bg-white rounded-lg border border-gray-200 p-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="text-center md:text-left">
                  <p class="text-sm text-gray-500">
                    {{ $t('HeadContract.IndirectProgressClaimsIndex.totalDevelopmentCosts') }}
                  </p>

                  <p class="text-lg font-semibold text-gray-900">
                    {{ australianCurrency(totalBudget) }}
                  </p>
                </div>

                <div class="text-center md:text-right">
                  <p class="text-sm text-gray-500">
                    {{ $t('HeadContract.IndirectProgressClaimsIndex.totalCumulativeCertified') }}
                  </p>

                  <p class="text-lg font-semibold text-gray-900">
                    {{ australianCurrency(totalCertified) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.table.claim') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.table.period') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.table.created') }}
                </th>

                <th class="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.table.certified') }}
                </th>

                <th class="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.table.certifiedAmount') }}
                </th>

                <th class="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {{ $t('HeadContract.IndirectProgressClaimsIndex.table.cumulativeCertified') }}
                </th>
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-100">
              <tr
                v-for="(projectClaim, i) in claims"
                :key="projectClaim.id"
                class="hover:bg-gray-50 transition-colors duration-150 ease-in-out cursor-pointer"
                @click="router.push({ name: 'IndirectProgressClaimsShow', params: { claimId: projectClaim.id }})"
              >
                <td class="px-6 py-5">
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                      </div>
                    </div>

                    <div class="flex-1 min-w-0">
                      <div class="flex items-center space-x-2">
                        <p class="text-sm font-medium text-gray-900 truncate">
                          {{ $t('HeadContract.IndirectProgressClaimsIndex.claimNumber', { claimNumber: projectClaim.claim_number }) }}
                        </p>

                        <span
                          v-if="overallStatus(projectClaim.status)"
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="overallStatus(projectClaim.status).colour"
                        >
                          <span
                            class="w-1.5 h-1.5 rounded-full mr-1.5"
                            :class="overallStatus(projectClaim.status).dot"
                          />
                          {{ overallStatus(projectClaim.status).label }}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                <td class="px-6 py-5 whitespace-nowrap">
                  <i18n-t
                    keypath="HeadContract.IndirectProgressClaimsIndex.claimPeriod"
                    tag="div"
                    class="text-sm text-gray-500"
                  >
                    <template #startDate>
                      <div class="text-sm text-gray-900">
                        {{ new Date(projectClaim.start).toDateString() }}
                      </div>
                    </template>

                    <template #endDate>
                      <span class="text-sm text-gray-500">
                        {{ new Date(projectClaim.end).toDateString() }}
                      </span>
                    </template>
                  </i18n-t>
                </td>

                <td class="px-6 py-5 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ projectClaim.createdAt.toLocaleString() }}
                  </div>
                </td>

                <td class="px-6 py-5 whitespace-nowrap">
                  <div
                    v-if="projectClaim.submittedAt"
                    class="text-sm text-gray-900"
                  >
                    {{ projectClaim.submittedAt.toLocaleString() }}
                  </div>

                  <div
                    v-else
                    class="text-sm text-gray-400 italic"
                  >
                    {{ $t('HeadContract.IndirectProgressClaimsIndex.notCertified') }}
                  </div>
                </td>

                <td class="px-6 py-5 whitespace-nowrap text-right">
                  <div
                    v-if="projectClaim.status === ClaimStatus.certified"
                    class="text-sm font-medium text-gray-900"
                  >
                    {{ australianCurrency(projectClaim.totalCertified) }}
                  </div>

                  <div
                    v-else
                    class="text-sm text-gray-400 italic"
                  >
                    -
                  </div>
                </td>

                <td class="px-6 py-5 whitespace-nowrap text-right">
                  <div
                    v-if="projectClaim.status === ClaimStatus.certified"
                    class="text-sm font-medium text-gray-900"
                  >
                    {{ australianCurrency(cumulativeCertified[i - numberOfDraftClaims]) }}
                  </div>

                  <div
                    v-else
                    class="text-sm text-gray-400 italic"
                  >
                    -
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <CreateClaimModal
      v-if="show"
      :project-id="$props.projectId"
      :latest-claim="latestClaim"
      boq-type="indirect"
      @close="show = false"
    />
  </div>
</template>

<script setup lang="ts">
import CreateClaimModal from '@/components/ProgressClaims/CreateClaimModal.vue';
import { useProjectTypeRedirect } from '@/composables/useProjectTypeRedirect';
import { australianCurrency, cumulativeSum, enumKeyFromValue } from '@/helpers';
import { t } from '@/i18n';
import { ClaimStatus, ClaimStatusTitles } from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useTradesStore } from '@/store/trades';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
}>();

const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const router = useRouter();

watch(() => [props.projectId], () => {
  claimsStore.fetchClaims(props.projectId, { boq_type: 'indirect' });
  tradesStore.fetchTrades(props.projectId, { boq_type: 'indirect' });
}, { immediate: true });

const show = ref(false);

const claims = computed(() => {
  return claimsStore.models.where('boq_type', 'indirect').where('project_id', parseInt(props.projectId)).orderBy(
    'claim_number',
    'desc',
  ).orderBy(
    'claim_number',
    'desc',
  ).get();
});

const overallStatus = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (true) {
    case status === ClaimStatus.certified:
      return {
        colour: 'bg-green-100 text-green-800',
        label: t('Models.Claim.statusTitles.certified'),
        dot: 'bg-green-400',
      };

    default:
      return {
        colour: 'bg-red-100 text-red-800',
        label: t('Models.Claim.statusTitles.draft'),
        dot: 'bg-red-400',
      };
  }
};

const latestClaim = computed(() => {
  if(claims.value.length) {
    return claims.value[0];
  }

  return undefined;
});

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items')
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', 'indirect')
    .get();
});

const totalBudget = computed(() => {
  return trades.value.reduce((total, trade) => {
    return total + trade.totalBudget;
  }, 0);
});

const totalCertified = computed(() => {
  return claims.value.filter((claim) => claim.status === ClaimStatus.certified).reduce((total, claim) => {
    return total += claim.totalCertified ?? 0;
  }, 0);
});

const cumulativeCertified = computed(() => {
  return cumulativeSum(
    claims.value.filter((claim) => claim.status === ClaimStatus.certified).map((claim) => claim.totalCertified)
      .reverse(),
  ).reverse();
});

const numberOfDraftClaims = computed(() => {
  return claims.value.filter((claim) => claim.status === ClaimStatus.draft).length;
});

useProjectTypeRedirect();
</script>

<style scoped></style>
